import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import ProgressButton from '@/components/common/progress-button';
import { FieldMessage } from '@/components/fields/field-message';
import PasswordField from '@/components/fields/password-field';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useResetPassword from '@/hooks/auth/use-reset-password';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import ROUTES from '@/routes';
import { addToast } from '@/stores/toast';

export default function UpdatePasswordRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutateAsync: resetPassword, isPending } = useResetPassword();

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordFromScheme),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  useAdjustLayout();

  const handleResetPassword = async (data: ForgotPasswordFormData) => {
    if (!data.password) return;

    const result = await resetPassword(data.password);

    addToast({
      type: result.Code === 0 ? 'success' : 'error',
      message: result.Code === 0 ? t('security.password-set-success') : result.Msg,
    });

    if (result.Code === 0) {
      navigate(ROUTES.MY.SETTINGS.SECURITY.$path(), { replace: true });
    }
  };

  return (
    <PageLayout
      title={t('auth.reset-password')}
      headerConfig={{
        navigateBack: true,
      }}>
      <form
        onSubmit={form.handleSubmit(handleResetPassword)}
        className='mx-5 flex flex-1 flex-col justify-between gap-4'>
        <div>
          <div className='form-control w-full'>
            <PasswordField
              id='password'
              className='bg-neutral-0'
              placeholder={t('auth.password')}
              {...form.register('password')}
            />

            <FieldMessage
              name='password'
              errors={form.formState.errors}
            />
          </div>

          <div className='form-control mt-4 w-full'>
            <PasswordField
              id='confirmPassword'
              className='bg-neutral-0'
              placeholder={t('auth.re-enter-password')}
              {...form.register('confirmPassword')}
            />
          </div>

          <FieldMessage
            name='confirmPassword'
            errors={form.formState.errors}
          />
        </div>

        <ProgressButton
          as='submit'
          isLoading={isPending}
          disabled={!form.formState.isDirty}
          className='btn btn-primary'
          label='common.confirm'
        />
      </form>
    </PageLayout>
  );
}

const forgotPasswordFromScheme = z
  .object({
    password: z.string().min(6, 'errors.string.length|count=6'),
    confirmPassword: z.string().min(6, 'errors.string.length|count=6'),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'errors.password-not-matched',
    path: ['confirmPassword'],
  });

type ForgotPasswordFormData = z.infer<typeof forgotPasswordFromScheme>;
